// src/pages/brand/BrandKit.tsx
import { useEffect, useMemo, useState, useCallback, type ReactNode } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

import {
  defaultBrandKit,
  type BrandKit as BrandKitType,
  type BrandTone,
  type BrandVisualStyle,
  type BrandImageType,
} from "../../types/brand";

import { type PlanKey, canPersistBrandKit, getBrandKitsLimit, BRAND_LIMIT_UNLIMITED } from "../../config/plans";

import { useBrandStore } from "../../hooks/useBrandStore";
import UpgradeModal from "../../components/UpgradeModal";

// ✅ Context real (plan/créditos) desde AppShell
import type { ModeShellContext } from "../../components/layout/AppShell";

// ✅ Dropdown premium unificado
import OvylonSelect from "../../components/ui/OvylonSelect";

const VISUAL_STYLES: BrandVisualStyle[] = [
  "photoreal",
  "editorial",
  "minimal",
  "illustration",
  "3d",
  "futuristic",
  "vintage",
];
const TONES: BrandTone[] = ["professional", "friendly", "bold", "luxury", "playful", "minimal"];
const IMAGE_TYPES: BrandImageType[] = ["lifestyle", "product", "mockup", "portrait", "abstract"];

function prettyPlan(plan: PlanKey) {
  if (plan === "free") return "Free";
  if (plan === "basic") return "Basic";
  if (plan === "pro") return "Pro";
  return "Premium";
}

function yesNo(v: boolean) {
  return v ? "✅" : "❌";
}

function Modal({
  open,
  title,
  children,
  onClose,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={onClose}>
      <div
        className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#0c0c14] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Brand Mode</div>
            <div className="text-lg font-bold text-white mt-2">{title}</div>
          </div>

          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition text-xs text-gray-200"
          >
            ✕
          </button>
        </div>

        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

// ✅ Toast flotante (con X) para feedback de guardado
function Toast({ open, text, onClose }: { open: boolean; text: string; onClose: () => void }) {
  if (!open) return null;
  return (
    <div className="fixed bottom-6 right-6 z-[60]">
      <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/70 backdrop-blur px-4 py-3 shadow-xl w-[320px]">
        <div className="text-emerald-300 text-lg leading-none">✅</div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-white">Listo</div>
          <div className="text-xs text-gray-300 mt-0.5">{text}</div>
        </div>
        <button
          onClick={onClose}
          className="text-xs px-2 py-1 rounded-lg border border-white/10 hover:bg-white/10 transition text-gray-200"
          title="Cerrar"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

// ✅ Switch visual por plan, pero clickable cuando está OFF para abrir upgrade.
function Toggle({
  checked,
  lockedReason,
  onAttemptEnable,
}: {
  checked: boolean;
  lockedReason?: string;
  onAttemptEnable?: () => void;
}) {
  const [nudge, setNudge] = useState(false);

  const isLockedOff = !checked && !!lockedReason;

  const handleClick = () => {
    if (!isLockedOff) return;
    setNudge(true);
    window.setTimeout(() => setNudge(false), 180);
    onAttemptEnable?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={checked ? "Activo en tu plan" : lockedReason || "No disponible"}
      className={`relative inline-flex h-6 w-11 items-center rounded-full border transition
        ${checked ? "bg-emerald-500/20 border-emerald-400/30" : "bg-white/5 border-white/10"}
        ${isLockedOff ? "cursor-pointer" : "cursor-default opacity-80"}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition
          ${checked ? "translate-x-5" : "translate-x-1"}
          ${nudge ? "scale-95" : "scale-100"}`}
      />
    </button>
  );
}

export default function BrandKit() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // ✅ Fuente de verdad: viene del AppShell (que lee /api/me)
  const outlet = useOutletContext<ModeShellContext>();
  const userPlan: PlanKey = outlet?.mePlan || "free";
  const credits = outlet?.credits ?? null;

  const allowPersist = canPersistBrandKit(userPlan); // Basic+
  const allowAdvanced = userPlan === "pro" || userPlan === "premium"; // Pro+

  const userKey = user ? user.email || user.id : null;

  const { store, setActiveBrand, createBrand, updateActiveBrand, deleteBrand, limit, limitReached } = useBrandStore(
    userKey,
    userPlan
  );

  const activeBrand = useMemo(() => {
    return (store as any)?.brands?.find((b: any) => String(b.id) === String((store as any)?.activeBrandId)) || null;
  }, [store]);

  // ✅ Modal Upgrade
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeCfg, setUpgradeCfg] = useState<{
    title: string;
    requiredPlan: PlanKey;
    feature: string;
    bullets: string[];
  } | null>(null);

  const openUpgrade = useCallback(
    (cfg: { title: string; requiredPlan: PlanKey; feature: string; bullets: string[] }) => {
      setUpgradeCfg(cfg);
      setUpgradeOpen(true);
    },
    []
  );

  // ✅ Brand Manager modal
  const [manageOpen, setManageOpen] = useState(false);

  // ✅ Toast
  const [toastOpen, setToastOpen] = useState(false);
  const [toastText, setToastText] = useState("");

  const showToast = (text: string) => {
    setToastText(text);
    setToastOpen(true);
    window.setTimeout(() => setToastOpen(false), 1500);
  };

  // local editable state (sync con activeBrand)
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [kit, setKit] = useState<BrandKitType>(defaultBrandKit());
  const [mood, setMood] = useState("");
  const [referenceUrl, setReferenceUrl] = useState("");

  useEffect(() => {
    if (!activeBrand) return;

    setName(activeBrand.name || "Mi marca");
    setDescription(activeBrand.description || "");
    setKit(activeBrand.kit ?? defaultBrandKit());
    setMood(activeBrand.advanced?.mood || "");
    setReferenceUrl(activeBrand.advanced?.referenceUrl || "");
  }, [activeBrand]);

  const updateColor = (idx: number, value: string) => {
    setKit((prev) => {
      const colors = [...(prev.colors || [])];
      colors[idx] = value;
      return { ...prev, colors };
    });
  };

  const addColor = () => {
    setKit((prev) => {
      const colors = [...(prev.colors || [])];
      if (colors.length >= 6) return prev;
      colors.push("#111827");
      return { ...prev, colors };
    });
  };

  const removeColor = (idx: number) => {
    setKit((prev) => {
      const colors = [...(prev.colors || [])].filter((_, i) => i !== idx);
      return { ...prev, colors: colors.length ? colors : ["#111827"] };
    });
  };

  const save = () => {
    if (!store || !activeBrand) return;

    // ✅ Free: solo name + description
    if (!allowPersist) {
      updateActiveBrand({
        name: name.trim() || "Mi marca",
        description: description.trim(),
      });
      showToast("Cambios guardados");
      return;
    }

    const patch: any = {
      name: name.trim() || "Mi marca",
      description: description.trim(),
      kit: {
        ...kit,
        brandName: name.trim() || kit.brandName || "Mi marca",
      },
    };

    if (allowAdvanced) {
      patch.advanced = { mood: mood.trim(), referenceUrl: referenceUrl.trim() };
    }

    updateActiveBrand(patch);
    showToast("Cambios guardados");
  };

  const canCreateMoreBrands = useMemo(() => {
    if (!store) return false;
    if (!Number.isFinite(limit)) return true;
    return (store as any).brands.length < limit;
  }, [store, limit]);

  const brandsLimitLabel = useMemo(() => {
    const lim = getBrandKitsLimit(userPlan);
    return lim >= BRAND_LIMIT_UNLIMITED ? "Ilimitadas" : String(lim);
  }, [userPlan]);

  const planFacts = useMemo(() => {
    return {
      plan: prettyPlan(userPlan),
      brands: brandsLimitLabel,
      persist: yesNo(allowPersist),
      advanced: yesNo(allowAdvanced),
    };
  }, [userPlan, brandsLimitLabel, allowPersist, allowAdvanced]);

  const UpgradeBtn = ({ onClick, label }: { onClick: () => void; label: string }) => (
    <button
      type="button"
      onClick={onClick}
      className="text-xs px-3 py-2 rounded-xl border border-indigo-400/25 bg-indigo-500/10 hover:bg-indigo-500/15 transition"
    >
      {label}
    </button>
  );

  const requiredPlanForMoreBrands: PlanKey = useMemo(() => {
    if (userPlan === "pro") return "premium";
    if (userPlan === "premium") return "premium";
    return "pro";
  }, [userPlan]);

  const brands = useMemo(() => {
    const list = (store as any)?.brands;
    return Array.isArray(list) ? list : [];
  }, [store]);

  const activeId = useMemo(() => String((store as any)?.activeBrandId || ""), [store]);

  const doCreateBrand = () => {
    const res = createBrand({ name: "Nueva marca", description: "" });
    if (!res.ok) {
      openUpgrade({
        title: "Crear más brands",
        requiredPlan: requiredPlanForMoreBrands,
        feature: "brand_multiple",
        bullets: [
          "Más brands para clientes/campañas simultáneas",
          "Mejor operación si trabajas como freelancer/agencia",
          "Organización más limpia por cliente",
        ],
      });
      return;
    }
    showToast("Brand creada");
  };

  const doDeleteBrand = (id: string) => {
    if (String(id) === String(activeId)) {
      showToast("Primero cambia la brand activa");
      return;
    }
    const ok = window.confirm("¿Eliminar esta brand? Esta acción no se puede deshacer.");
    if (!ok) return;

    const res = deleteBrand(id);
    if (!res.ok) {
      if (res.reason === "min_one_brand") showToast("Debes tener al menos 1 brand");
      else showToast("No se pudo eliminar");
      return;
    }
    showToast("Brand eliminada");
  };

  // ✅ options para selects premium
  const brandOptions = useMemo(() => brands.map((b: any) => ({ value: String(b.id), label: String(b.name || "Brand") })), [brands]);
  const visualStyleOptions = useMemo(() => VISUAL_STYLES.map((s) => ({ value: s, label: s })), []);
  const toneOptions = useMemo(() => TONES.map((t) => ({ value: t, label: t })), []);
  const imageTypeOptions = useMemo(() => IMAGE_TYPES.map((t) => ({ value: t, label: t })), []);

  return (
    <section className="max-w-5xl">
      <Toast open={toastOpen} text={toastText} onClose={() => setToastOpen(false)} />

      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gray-400">BRAND MODE</div>
          <h1 className="text-3xl font-bold mt-2">Brand Kit</h1>
          <p className="text-sm text-gray-400 mt-2">Define tu identidad: nombre, descripción y consistencia visual.</p>
        </div>
      </div>

      {/* Plan strip */}
      <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Estado</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 bg-black/30">
                Plan: <span className="text-gray-100 font-medium">{planFacts.plan}</span>
              </span>

              <span className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 bg-black/30">
                Créditos: <span className="text-gray-100 font-medium">{credits === null ? "…" : String(credits)}</span>
              </span>

              <span className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 bg-black/30">
                Brands: <span className="text-gray-100 font-medium">{planFacts.brands}</span>
              </span>
              <span className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 bg-black/30">
                Persistencia: <span className="text-gray-100 font-medium">{planFacts.persist}</span>
              </span>
              <span className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 bg-black/30">
                Advanced: <span className="text-gray-100 font-medium">{planFacts.advanced}</span>
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setManageOpen(true)}
              className="px-4 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-sm"
            >
              Gestionar brands
            </button>

            <button
              type="button"
              onClick={doCreateBrand}
              disabled={!canCreateMoreBrands}
              className="px-4 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              title={limitReached ? "Límite alcanzado para tu plan" : ""}
            >
              + Crear brand
            </button>

            {allowAdvanced && userPlan === "pro" && (
              <UpgradeBtn
                label="Gestionar plan"
                onClick={() =>
                  openUpgrade({
                    title: "Brands ilimitadas + todo desbloqueado",
                    requiredPlan: "premium",
                    feature: "brand_multiple",
                    bullets: ["Brands ilimitadas (agencias / múltiples clientes)", "Mejor operación en alto volumen"],
                  })
                }
              />
            )}

            {!allowPersist && (
              <UpgradeBtn
                label="Upgrade a Basic"
                onClick={() =>
                  openUpgrade({
                    title: "Guardar consistencia (Brand Kit)",
                    requiredPlan: "basic",
                    feature: "brand_persist",
                    bullets: [
                      "Guardar y reutilizar colores / estilo / tono",
                      "Consistencia real en campañas completas",
                      "Menos trabajo manual repitiendo configuraciones",
                    ],
                  })
                }
              />
            )}

            {allowPersist && !allowAdvanced && (
              <UpgradeBtn
                label="Upgrade a Pro"
                onClick={() =>
                  openUpgrade({
                    title: "Desbloquear Advanced + 3 brands",
                    requiredPlan: "pro",
                    feature: "brand_advanced_fields",
                    bullets: ["Campos avanzados: mood / referencia", "Hasta 3 brands activas", "Ideal para freelancers"],
                  })
                }
              />
            )}
          </div>
        </div>
      </div>

      {/* Selector brand activa (premium) */}
      {store && brands.length > 1 && (
        <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-5">
          <OvylonSelect
            label="Brand activa"
            value={activeId}
            onChange={(v) => setActiveBrand(v)}
            options={brandOptions}
            placeholder="Selecciona una brand…"
            className="mt-1"
          />
          <p className="text-[11px] text-gray-500 mt-2">La brand activa se usará por defecto en tus creaciones.</p>
        </div>
      )}

      {/* Form */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-6">
        <div>
          <label className="block text-xs text-gray-400 mb-2">Nombre de marca</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej: Ovylon, Mi Agencia, FitPro..."
            className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-2">Descripción corta</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ej: Agencia de performance marketing para e-commerce premium"
            className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40"
          />
        </div>

        {/* Consistencia */}
        <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Consistencia</div>
              <div className="mt-1 text-sm text-gray-300">Colores, estilo, tono y tipo de imagen.</div>
            </div>

            <div className="flex items-center gap-3">
              {!allowPersist ? <span className="text-[10px] px-2 py-1 rounded-full border border-white/10 bg-black/30 text-gray-200">Bloqueado</span> : (
                <span className="text-[10px] px-2 py-1 rounded-full border border-white/10 bg-black/30 text-gray-200">Activo</span>
              )}
              <Toggle
                checked={allowPersist}
                lockedReason={!allowPersist ? "Disponible desde Basic" : undefined}
                onAttemptEnable={() =>
                  openUpgrade({
                    title: "Guardar consistencia (Brand Kit)",
                    requiredPlan: "basic",
                    feature: "brand_persist",
                    bullets: [
                      "Guardar y reutilizar colores / estilo / tono",
                      "Consistencia real en campañas completas",
                      "Menos trabajo manual repitiendo configuraciones",
                    ],
                  })
                }
              />
            </div>
          </div>

          <div className={`mt-5 space-y-6 ${!allowPersist ? "opacity-50 pointer-events-none select-none" : ""}`}>
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <label className="block text-xs text-gray-400">Colores</label>
                <button
                  type="button"
                  onClick={addColor}
                  className="text-xs px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition"
                >
                  + Añadir color
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(kit.colors || []).map((c, idx) => (
                  <div key={`${c}-${idx}`} className="flex items-center gap-3">
                    <input
                      type="color"
                      value={c}
                      onChange={(e) => updateColor(idx, e.target.value)}
                      className="h-10 w-12 rounded-lg border border-white/10 bg-black/30"
                    />
                    <input
                      value={c}
                      onChange={(e) => updateColor(idx, e.target.value)}
                      placeholder="Ej: #0A07D5"
                      className="flex-1 px-3 py-2.5 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeColor(idx)}
                      className="px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-xs"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <p className="text-[11px] text-gray-500 mt-2">Recomendado: 3–5 colores.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <OvylonSelect
                label="Estilo visual"
                value={kit.visualStyle}
                onChange={(v) => setKit((p) => ({ ...p, visualStyle: v as BrandVisualStyle }))}
                options={visualStyleOptions}
                className="mt-1"
              />

              <OvylonSelect
                label="Tono"
                value={kit.tone}
                onChange={(v) => setKit((p) => ({ ...p, tone: v as BrandTone }))}
                options={toneOptions}
                className="mt-1"
              />

              <OvylonSelect
                label="Tipo de imagen"
                value={kit.imageType}
                onChange={(v) => setKit((p) => ({ ...p, imageType: v as BrandImageType }))}
                options={imageTypeOptions}
                className="mt-1"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-2">Notas (opcional)</label>
              <textarea
                value={kit.notes || ""}
                onChange={(e) => setKit((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Ej: evitar texto en la imagen, estilo premium, iluminación suave..."
                className="w-full min-h-[110px] px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Advanced */}
        <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Advanced</div>
              <div className="mt-1 text-sm text-gray-300">Mood y referencia (para campañas más consistentes).</div>
            </div>

            <div className="flex items-center gap-3">
              {!allowAdvanced ? (
                <span className="text-[10px] px-2 py-1 rounded-full border border-white/10 bg-black/30 text-gray-200">
                  Bloqueado
                </span>
              ) : (
                <span className="text-[10px] px-2 py-1 rounded-full border border-white/10 bg-black/30 text-gray-200">
                  Activo
                </span>
              )}
              <Toggle
                checked={allowAdvanced}
                lockedReason={!allowAdvanced ? "Disponible desde Pro" : undefined}
                onAttemptEnable={() =>
                  openUpgrade({
                    title: "Desbloquear Advanced + 3 brands",
                    requiredPlan: "pro",
                    feature: "brand_advanced_fields",
                    bullets: ["Campos avanzados: mood / referencia", "Hasta 3 brands activas", "Ideal para freelancers"],
                  })
                }
              />
            </div>
          </div>

          <div className={`mt-5 ${!allowAdvanced ? "opacity-50 pointer-events-none select-none" : ""}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-2">Mood / estilo guía</label>
                <input
                  value={mood}
                  onChange={(e) => setMood(e.target.value)}
                  placeholder="Ej: clean premium, editorial minimal, bold neon..."
                  className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-2">Referencia (URL)</label>
                <input
                  value={referenceUrl}
                  onChange={(e) => setReferenceUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500/40"
                />
              </div>
            </div>

            <p className="text-[11px] text-gray-500 mt-3">
              Esto ayuda mucho cuando haces campañas completas (freelancers/agencias).
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button
            onClick={() => navigate("/brand")}
            className="px-5 py-3 rounded-xl border border-white/10 hover:bg-white/10 transition text-sm"
          >
            Volver
          </button>

          <button
            onClick={() => {
              save();
              if (!allowPersist) {
                openUpgrade({
                  title: "Guardar consistencia (Brand Kit)",
                  requiredPlan: "basic",
                  feature: "brand_persist",
                  bullets: [
                    "Guardar y reutilizar colores / estilo / tono",
                    "Consistencia real en campañas completas",
                    "Menos trabajo manual repitiendo configuraciones",
                  ],
                });
              }
            }}
            className="px-5 py-3 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 transition shadow-lg shadow-indigo-500/30"
          >
            Guardar cambios
          </button>
        </div>
      </div>

      {/* ✅ Brand Manager Modal */}
      <Modal open={manageOpen} title="Gestionar brands" onClose={() => setManageOpen(false)}>
        <div className="space-y-4">
          <div className="text-sm text-gray-300">
            Aquí puedes ver tus brands, cambiar la activa y eliminar las que no uses.
          </div>

          <div className="space-y-2">
            {brands.map((b: any) => {
              const isActive = String(b.id) === String(activeId);
              const cannotDelete = isActive || brands.length <= 1;

              return (
                <div
                  key={b.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 p-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{b.name}</div>
                    <div className="text-[11px] text-gray-400 truncate">{b.description || "Sin descripción"}</div>
                  </div>

                  <div className="flex items-center gap-2">
                    {isActive && (
                      <span className="text-[10px] px-2 py-1 rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-200">
                        Activa
                      </span>
                    )}

                    {!isActive && (
                      <button
                        type="button"
                        onClick={() => setActiveBrand(String(b.id))}
                        className="px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-xs"
                      >
                        Usar
                      </button>
                    )}

                    <button
                      type="button"
                      disabled={cannotDelete}
                      title={
                        isActive
                          ? "No puedes eliminar la brand activa"
                          : brands.length <= 1
                          ? "Debes tener al menos una brand"
                          : "Eliminar"
                      }
                      onClick={() => doDeleteBrand(String(b.id))}
                      className="px-3 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-2">
            <button
              type="button"
              onClick={() => setManageOpen(false)}
              className="px-4 py-2 rounded-xl border border-white/10 hover:bg-white/10 transition text-sm"
            >
              Cerrar
            </button>

            <button
              type="button"
              onClick={doCreateBrand}
              disabled={!canCreateMoreBrands}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400 transition shadow-lg shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              title={limitReached ? "Límite alcanzado para tu plan" : ""}
            >
              + Crear brand
            </button>
          </div>

          {limitReached && <div className="text-[11px] text-gray-500">Límite alcanzado. Crea más brands mejorando tu plan.</div>}
        </div>
      </Modal>

      {/* ✅ Modal Upgrade */}
      <UpgradeModal
        open={upgradeOpen && !!upgradeCfg}
        onClose={() => setUpgradeOpen(false)}
        title={upgradeCfg?.title || "Actualizar plan"}
        requiredPlan={upgradeCfg?.requiredPlan || "basic"}
        feature={upgradeCfg?.feature || "brand_persist"}
        from="/brand/kit"
        bullets={upgradeCfg?.bullets || []}
      />
    </section>
  );
}



